"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParentRatingRowCollector = exports.ParentRatingRows = exports.ParentRatingRowIds = exports.ParentRatingRowIdPrefix = exports.ParentRatingRowSchema = exports.DatasheetTableRowRefSchema = exports.SourceRowAssociation = void 0;
const z = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
var SourceRowAssociation;
(function (SourceRowAssociation) {
    SourceRowAssociation["Cited"] = "cited";
    SourceRowAssociation["Associated"] = "associated";
})(SourceRowAssociation || (exports.SourceRowAssociation = SourceRowAssociation = {}));
exports.DatasheetTableRowRefSchema = z.object({
    provider: z.string().nullable(),
    pageNumber: z.number().int().min(1),
    elementIndex: z.number().int().min(0),
    rowIndex: z.number().int().min(0),
    how: z.nativeEnum(SourceRowAssociation).optional(),
});
exports.ParentRatingRowSchema = z.object({
    id: z.string().min(1),
    description: z.string(),
    symbol: z.string().nullable(),
    min: z.string().nullable(),
    typ: z.string().nullable(),
    max: z.string().nullable(),
    boundaryIds: z.array(z.string()),
    sourceRows: z.array(exports.DatasheetTableRowRefSchema).optional(),
});
var ParentRatingRowIdPrefix;
(function (ParentRatingRowIdPrefix) {
    ParentRatingRowIdPrefix["Amr"] = "amr_row_";
    ParentRatingRowIdPrefix["Oc"] = "oc_row_";
})(ParentRatingRowIdPrefix || (exports.ParentRatingRowIdPrefix = ParentRatingRowIdPrefix = {}));
class ParentRatingRowIds {
    static { this.HASH_HEX_LENGTH = 16; }
    static { this.RANDOM_LENGTH = 16; }
    static async fromSourceRows(prefix, refs, ordinal) {
        if (refs.length === 0) {
            throw new Error('A parent rating row id can be derived only from at least one source row.');
        }
        const digest = await ParentRatingRowIds.sha256Hex(ParentRatingRowIds.sourceRowsKey(refs));
        const suffix = ordinal > 0 ? `_${ordinal}` : '';
        return prefix + digest.slice(0, ParentRatingRowIds.HASH_HEX_LENGTH) + suffix;
    }
    static random(prefix) {
        return prefix + sigma_engine_shared_1.Utils.randomAlphaStr(ParentRatingRowIds.RANDOM_LENGTH);
    }
    static async forRows(prefix, rows) {
        const ordinals = new Map();
        const seen = new Map();
        for (const row of [...rows].sort((a, b) => a.position - b.position)) {
            if (row.sourceRows.length === 0) {
                continue;
            }
            const key = ParentRatingRowIds.sourceRowsKey(row.sourceRows);
            const ordinal = seen.get(key) ?? 0;
            seen.set(key, ordinal + 1);
            ordinals.set(row, ordinal);
        }
        return Promise.all(rows.map(async (row) => {
            const ordinal = ordinals.get(row);
            const id = ordinal === undefined ?
                ParentRatingRowIds.random(prefix)
                : await ParentRatingRowIds.fromSourceRows(prefix, row.sourceRows, ordinal);
            return { row, id };
        }));
    }
    static sourceRowsKey(refs) {
        const parts = refs.map(({ provider, pageNumber, elementIndex, rowIndex }) => [provider ?? '', pageNumber, elementIndex, rowIndex].join('|'));
        return [...new Set(parts)].sort().join('\n');
    }
    static async sha256Hex(input) {
        const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
        return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
}
exports.ParentRatingRowIds = ParentRatingRowIds;
class ParentRatingRows {
    static pruneBoundaryIds(rows, boundaries) {
        if (rows === undefined) {
            return undefined;
        }
        const existing = new Set(boundaries.map((boundary) => boundary.id));
        const pruned = rows.map((row) => {
            const boundaryIds = row.boundaryIds.filter((id) => existing.has(id));
            return boundaryIds.length === row.boundaryIds.length ? row : { ...row, boundaryIds };
        });
        return pruned.every((row, index) => row === rows[index]) ? rows : pruned;
    }
}
exports.ParentRatingRows = ParentRatingRows;
class ParentRatingRowCollector {
    constructor() {
        this.rows = new Map();
    }
    record(origin, boundaryIds) {
        if (origin === undefined) {
            return;
        }
        const entry = this.rows.get(origin.id) ?? { origin, boundaryIds: [] };
        entry.boundaryIds.push(...boundaryIds);
        this.rows.set(origin.id, entry);
    }
    toRows() {
        return [...this.rows.values()]
            .filter((entry) => entry.boundaryIds.length > 0)
            .sort((a, b) => a.origin.position - b.origin.position)
            .map(({ origin, boundaryIds }) => {
            const { position, ...row } = origin;
            return { ...row, boundaryIds };
        });
    }
}
exports.ParentRatingRowCollector = ParentRatingRowCollector;
//# sourceMappingURL=parent-rating-row.js.map